import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    handicap: '',
    phone: '',
    invitation_code: '',
    subscribe_newsletter: true,
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/the-approach');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        handicap: formData.handicap ? parseFloat(formData.handicap) : null,
        invitation_code: formData.invitation_code || null,
      };
      const userData = await register(data);
      void userData;
      toast.success('Welcome! Your free account is ready.');
      // Skip plan selection while subscriptions are paused — go straight to the educational content
      navigate('/the-approach');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0E9] flex" data-testid="register-page">
      {/* Left - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/6573665/pexels-photo-6573665.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Golf course at sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2C5530]/40"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium mb-4">
            "The journey to a quieter mind starts here."
          </blockquote>
          <p className="text-white/80">Create your account to begin tracking your progress</p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
              alt="Quiet Mind Golf" 
              className="h-12 w-auto"
            />
            <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A]">
              Quiet Mind Golf
            </span>
          </Link>

          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">
            Create Account
          </h1>
          <p className="text-[#57534E] mb-6">
            Join to track your mental game journey
          </p>

          {/* Free access banner */}
          <div className="mb-6 bg-[#2C5530]/5 border border-[#2C5530]/30 rounded-md px-4 py-3" data-testid="free-access-banner">
            <p className="text-sm text-[#1A1A1A] leading-relaxed">
              <span className="font-semibold text-[#2C5530]">100% free —</span> all educational content (QMG Blueprint, Mind Factor, Acceptance vs. Resistance) is unlocked the moment you create your account. No payment, no card, no catch.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full bg-white"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-white"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full bg-white pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A1A1A]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="handicap" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Handicap
                </label>
                <input
                  type="number"
                  id="handicap"
                  name="handicap"
                  value={formData.handicap}
                  onChange={handleChange}
                  step="0.1"
                  min="-10"
                  max="54"
                  placeholder="e.g., 12.4"
                  className="w-full bg-white"
                  data-testid="register-handicap-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-white"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="invitation_code" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                Invitation Code <span className="text-[#737373] font-normal">(if you have one)</span>
              </label>
              <input
                type="text"
                id="invitation_code"
                name="invitation_code"
                value={formData.invitation_code}
                onChange={handleChange}
                placeholder="Enter your invitation code"
                className="w-full bg-white"
                data-testid="register-invitation-input"
              />
              <p className="text-xs text-[#737373] mt-1">
                With a valid invitation code, your account will be instantly approved.
              </p>
            </div>

            {/* Newsletter opt-in */}
            <label className="flex items-start gap-3 cursor-pointer pt-1" data-testid="newsletter-optin-label">
              <input
                type="checkbox"
                name="subscribe_newsletter"
                checked={formData.subscribe_newsletter}
                onChange={handleChange}
                className="mt-1 w-4 h-4 accent-[#2C5530] cursor-pointer"
                data-testid="register-newsletter-checkbox"
              />
              <span className="text-sm text-[#57534E] leading-relaxed">
                Send me occasional mental-game tips and updates by email. <span className="text-[#737373]">(You can unsubscribe anytime.)</span>
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-[#57534E]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2C5530] font-semibold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>

          <div className="mt-6 pt-6 border-t border-[#E7E5E4] text-center">
            <p className="text-sm text-[#57534E]">
              Are you a coach?{' '}
              <Link 
                to="/coach-login" 
                className="text-[#BC562B] font-semibold hover:underline"
                data-testid="coach-request-link"
              >
                Request coach access
              </Link>
            </p>
          </div>

          <Link
            to="/"
            className="block mt-6 text-center text-[#737373] hover:text-[#1A1A1A] text-sm"
            data-testid="back-home-link"
          >
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
